import React from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  POSITION_HEADER_SUFFIX,
  POSITION_HEADER_TOOLTIP,
} from "../../utils/positionFormatter";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import NationalityFlags from "../common/NationalityFlags";

const DiscoveryPlayersTable = ({
  loading,
  error,
  players,
  isFreeAgent,
  fetchData,
  currentPage,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const handlePlayerClick = (playerId) => {
    // Use sportmonks_id if available, otherwise use wyscout_id
    navigate(`/search/player/${playerId}`);
  };

  const handleClubClick = (clubId) => {
    if (!clubId) {
      toast.error("Club id not found");
      return;
    }
    navigate(`/search/club/${clubId}`);
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-12">
      <div className="bg-white rounded-lg shadow overflow-x-auto border border-[#D6DBEA]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading free agents...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-red-500 mb-2">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-gray-600">{error}</p>
              <button
                onClick={() => fetchData(currentPage)}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : players.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3"
                  />
                </svg>
              </div>
              <p className="text-gray-600">
                No free agents found with the current filters.
              </p>
            </div>
          </div>
        ) : (
          <table className="min-w-full table-sticky-first">
            <thead>
              <tr className="bg-[#F7F7F7] text-xs font-medium text-[#222A35] uppercase tracking-wider border-b border-[#D6DBEA]">
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  Player
                </th>
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  <span
                    className="inline-flex items-center gap-1"
                    title={POSITION_HEADER_TOOLTIP}
                  >
                    <span className="uppercase">Position</span>
                    <span className="text-[10px] text-[#667085] font-normal uppercase">
                      {POSITION_HEADER_SUFFIX}
                    </span>
                  </span>
                </th>
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  {isFreeAgent ? "Left Club" : "Club"}
                </th>
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  Country
                </th>
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  Age/DOB
                </th>
                <th className="px-6 py-3 text-left whitespace-nowrap">
                  Market Value
                </th>
                {isFreeAgent && (
                  <th className="px-6 py-3 text-left whitespace-nowrap">
                    Free Agent Since
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D6DBEA]">
              {players.map((player) => (
                <tr key={player.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => {
                        if (!player.sportmonks_id && !player.wyscout_id) {
                          toast.error("Player id not found");
                          return;
                        }
                        handlePlayerClick(
                          player.sportmonks_id || player.wyscout_id
                        );
                      }}
                      className="flex items-center gap-4 focus:outline-none"
                    >
                      {console.log(player, "this is player")}
                      {player.image_url ? (
                        <img
                          src={player.image_url}
                          alt=""
                          className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full object-cover border border-[#D6DBEA]`}
                        />
                      ) : (
                        <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full bg-gray-100 flex items-center justify-center`}>
                          <span className="text-sm font-medium text-gray-600">
                            {player.name
                              ? player.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                              : "FC"}
                          </span>
                        </div>
                      )}
                      <span className="font-bold text-[#222A35] text-xs sm:text-[16px] text-left">
                        {player.name}
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {player.positionDisplay}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isFreeAgent ? (
                      <div className="flex items-center gap-2">
                        {player.club.logo ? (
                          <img
                            src={player.club.logo}
                            alt=""
                            className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full object-cover border border-[#D6DBEA]`}
                          />
                        ) : (
                          <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-gray-200 flex items-center justify-center`}>
                            <span className="text-xs text-gray-500">FC</span>
                          </div>
                        )}
                        <span className="text-xs sm:text-[16px] text-[#222A35] font-normal">
                          {player.club.name}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleClubClick(player.club?.id)}
                        className="flex items-center gap-2 focus:outline-none"
                      >
                        {player.club.logo ? (
                          <img
                            src={player.club.logo}
                            alt=""
                            className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full object-cover border border-[#D6DBEA]`}
                          />
                        ) : (
                          <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full bg-gray-200 flex items-center justify-center`}>
                            <span className="text-xs text-gray-500">FC</span>
                          </div>
                        )}
                        <span className="text-xs sm:text-[16px] text-[#222A35] font-normal text-left">
                          {player.club.name}
                        </span>
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {/* All citizenships, national team first. This cell used
                        to render birth country and nationality back to back
                        with no de-dupe, so single-nationality players showed
                        the same flag twice. */}
                    <NationalityFlags
                      nationalities={player.nationalities}
                      className={`${isMobile ? 'w-4 h-3' : 'w-5 h-3.5'} rounded`}
                    />
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                    {player.age} / {player.date_of_birth}
                  </td>
                  <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-medium whitespace-nowrap">
                    {player.marketValue}
                  </td>
                  {isFreeAgent && (
                    <td className="px-6 py-4 text-xs sm:text-[16px] text-[#222A35] font-normal whitespace-nowrap">
                      {player.freeAgentDate}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DiscoveryPlayersTable;
